/**
 * Nested DnD container using `renderClone` to fix positioning issues
 * caused by @hello-pangea/dnd's lack of nested scroll container support.
 *
 * @see https://github.com/hello-pangea/dnd/blob/main/docs/guides/reparenting.md
 */

import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { observer } from "mobx-react-lite";

import { useChildMessageDragAndDrop } from "@/components/Playground/Messages/PlaceholderContainer/Content/hooks/useChildMessageDragAndDrop";
import { usePlaceholderContainer } from "@/components/Playground/Messages/PlaceholderContainer/context/placeholder-container-context";
import {
  createRenderDraggableItemClone,
  DraggableItem,
} from "@/components/Playground/Messages/PlaceholderContainer/dnd/DraggableItem";
import { DnDNestedContainerProps } from "@/components/Playground/Messages/PlaceholderContainer/dnd/types";
import { ContainerLevelError } from "@/components/Playground/Messages/PlaceholderContainer/errors/ContainerLevelError";
import { cn } from "@/lib/utils";

/**
 * A drag-and-drop container designed for use INSIDE another scrollable container.
 *
 * Uses the `renderClone` pattern to work around @hello-pangea/dnd's limitation
 * with nested scroll containers.
 */
export const DnDNestedContainer = observer(
  ({ messages, renderItem, onDragStart, onDragEnd }: DnDNestedContainerProps) => {
    const { handleDragEnd, droppableRef, droppableId, dragDisabled } = useChildMessageDragAndDrop();
    const { childStore } = usePlaceholderContainer();

    const handleDragEndWithCallback = (result: DropResult) => {
      handleDragEnd(result);
      onDragEnd?.();
    };

    // Access messages.length to ensure MobX tracks this observable array.
    // Without explicit property access, MobX won't register this component
    // as a dependency, and adding new messages won't trigger a re-render.
    const _messageCount = messages.length;

    const hasParseError = childStore.parseError !== null;

    return (
      <div className={cn("relative rounded-lg", hasParseError && "border-2 border-red-400")}>
        {/* Parse error banner */}
        {hasParseError && <ContainerLevelError childStore={childStore} />}

        <DragDropContext onDragStart={onDragStart} onDragEnd={handleDragEndWithCallback}>
          <Droppable
            droppableId={droppableId}
            renderClone={createRenderDraggableItemClone(messages, renderItem)}
          >
            {(provided, snapshot) => (
              <div
                key={_messageCount}
                ref={el => {
                  provided.innerRef(el);
                  droppableRef.current = el;
                }}
                {...provided.droppableProps}
                className={cn(
                  "flex flex-col gap-y-2 rounded-lg",
                  "transition-colors duration-200",
                  snapshot.isDraggingOver && "bg-blue-50/30",
                )}
              >
                {messages.map((message, index) => (
                  <Draggable
                    key={message.id}
                    draggableId={message.id}
                    index={index}
                    isDragDisabled={dragDisabled}
                  >
                    {({ innerRef, draggableProps, dragHandleProps }, { isDragging }) => (
                      <DraggableItem
                        innerRef={innerRef}
                        draggableProps={draggableProps}
                        dragHandleProps={dragHandleProps}
                        isDragging={isDragging}
                        isClone={false}
                        message={message}
                        index={index}
                        renderItem={renderItem}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  },
);
